import { atomFamily } from "recoil";
import { v4 as uuidv4 } from "uuid";

import { type MyLensesPreviewMedia, type PreviewMediaResource } from "../../gql-generated/types";
import { isVideoSrcExtension } from "../../lens-preview/validation";

import { DEFAULT_PREVIEW_NAME } from "./LensDetailsState";
import { StateKeys } from "./StateKeys";

export interface BaseLensPreviewMediaItem {
    // non-null for just uploaded files
    file: File | null;
    url: string;
    isVideo: boolean;
    name: string;
    isLensStudioPreview?: boolean;
}

export interface MediaIdItem extends BaseLensPreviewMediaItem {
    type: "media";
    mediaId: string;
    referenceId?: never;
}

export interface ReferenceIdItem extends BaseLensPreviewMediaItem {
    type: "reference";
    referenceId: string;
    mediaId?: never;
}

export interface DefaultItem extends BaseLensPreviewMediaItem {
    type: "default";
    referenceId?: never;
    mediaId?: never;
}

export type LensPreviewMediaItem = MediaIdItem | ReferenceIdItem | DefaultItem;

export const isMediaItem = (item: LensPreviewMediaItem): item is MediaIdItem => {
    return item.type === "media";
};

export const isReferenceItem = (item: LensPreviewMediaItem): item is ReferenceIdItem => {
    return item.type === "reference";
};

export const isDefaultItem = (item: LensPreviewMediaItem): item is DefaultItem => {
    return item.type === "default";
};

export const getItemId = (item: LensPreviewMediaItem | null): string => {
    if (!item) {
        return uuidv4();
    }

    if (isMediaItem(item)) {
        return item.mediaId;
    }
    if (isReferenceItem(item)) {
        return item.referenceId;
    }
    return uuidv4();
};

export const mapPreviewMediaItemsToResources = (items: LensPreviewMediaItem[]): PreviewMediaResource[] => {
    return items
        .filter((item) => item.type !== "default")
        .map((item): PreviewMediaResource => {
            if (isMediaItem(item)) {
                return {
                    mediaId: item.mediaId,
                };
            }
            return {
                uploadRefId: item.referenceId,
            };
        });
};

/**
 * Converts MyLensesPreviewMedia[] (GQL generated type) to LensPreviewMediaItem[] (local state type) for consistent preview media handling
 * across submission and lens details contexts.
 *
 * This utility standardizes the different preview media types used in:
 * - LensPreviewMediaItem: local state type (defined above)
 * - MyLensesPreviewMedia: media type on Lens object (gql type)
 * - PreviewMediaResource: media for a lens in submission flow (gql type)
 */
export const convertMyLensesPreviewMediaToItems = (
    previewMediaList: readonly MyLensesPreviewMedia[],
): LensPreviewMediaItem[] => {
    if (previewMediaList.length === 0) {
        return [];
    }

    return previewMediaList.map(
        (media): LensPreviewMediaItem => ({
            file: null,
            url: media.standardMediaUrl,
            isVideo: isVideoSrcExtension(media.standardMediaUrl),
            name: DEFAULT_PREVIEW_NAME,
            type: "media",
            mediaId: media.mediaId,
        }),
    );
};

export const defaultPreviewMediaItem: Readonly<LensPreviewMediaItem> = {
    file: null,
    url: "",
    name: "",
    isVideo: false,
    type: "default",
};

// Recoil atom kept for submission flows only - lens details uses Jotai change tracking

export const lensPreviewMediaItemsAtomById = atomFamily<LensPreviewMediaItem[], string>({
    key: StateKeys.LensPreviewMediaItems,
    default: [],
});

export const SPECTACLES_MAX_NUM_PREVIEWS = 4;

export const SNAPCHAT_MAX_NUM_PREVIEWS = 1;
